const MAX_MINIONS = 3;
// Hembryggaren grows at every turn end until it reaches this.
const BREW_CAP = 6;
// What a Smällkaramell makes whoever knocks it out drink.
const POPPER_SIPS = 3;
// What a Kylväska hands its owner back when it falls.
const COOLER_HEAL = 3;
// What an Isskulptur loses at every turn end.
const MELT = 2;
// Livlinan: reaching this many sips pulls you back LIFELINE_HEAL.
const LIFELINE_AT = 24;
const LIFELINE_HEAL = 5;
// Kontraskålen draws one card a sip, up to this many.
const COUNTER_DRAW_CAP = 2;

function sipsText(amount) {
  return `${amount} klunk${amount === 1 ? "" : "ar"}`;
}

// The damage/heal mutators the card library is built against. They live here rather
// than inside startApp() so the rules can be tested without a DOM, and so the card
// library and the tests exercise the same implementation.
// drawCard is what Fotografen and Kontraskålen draw with. pickRebound(target, source)
// answers who a Tvåvägsspegel sends an attack on to, or null for "back to the attacker"
// — it needs the table, which combat does not have. recordHeal and recordBlock count
// what a heal really took off and what an umbrella, a coaster or a two-way mirror really
// stopped, for the end screen's statistics.
export function createCombat({
  recordDrinkEvent = () => {},
  recordMinionEvent = () => {},
  recordHeal = () => {},
  recordBlock = () => {},
  makeId = () => `minion-${Math.random().toString(36).slice(2, 10)}`,
  drawCard = () => false,
  pickRebound = () => null,
} = {}) {
  let narration = [];

  function narrate(line) {
    narration.push(line);
  }

  function drainNarration() {
    const lines = narration;
    narration = [];
    return lines;
  }

  function addSips(player, amount, source = null) {
    if (!player || amount <= 0) return;
    // Dörrvakten: sips somebody else sends past the minions still meet the door first.
    // Fatigue has no source and your own toast is your own, so neither is stopped.
    if (source && source.id !== player.id) {
      const bouncer = player.minions.find((minion) => minion.ability === "bouncer" && minion.hp > 0);
      if (bouncer) {
        const soaked = Math.min(bouncer.hp, amount);
        strikeMinion(player, bouncer, soaked, source);
        amount -= soaked;
        if (amount <= 0) return;
      }
    }
    const before = player.sipsTaken;
    player.sipsTaken = Math.min(player.maxSips, player.sipsTaken + amount);
    const taken = player.sipsTaken - before;
    if (taken <= 0) return;
    recordDrinkEvent(player, taken, source, player.sipsTaken >= player.maxSips && before < player.maxSips);
    // Hämnden pays this back; the controller zeroes it as the player's turn ends.
    player.recentSips = (player.recentSips || 0) + taken;

    // Livlinan saves the evening, not the match: a hit that ends it is too late.
    if (player.lifeline && player.sipsTaken >= LIFELINE_AT && player.sipsTaken < player.maxSips) {
      player.lifeline = false;
      narrate(`${player.name} greppar livlinan — ${sipsText(LIFELINE_HEAL)} bort.`);
      removeSips(player, LIFELINE_HEAL);
    }
    if (player.counterDraw) {
      player.counterDraw = false;
      const draws = Math.min(COUNTER_DRAW_CAP, taken);
      narrate(`${player.name} skålar tillbaka och drar ${draws} kort.`);
      for (let draw = 0; draw < draws; draw += 1) drawCard(player);
    }

    // Reflect only triggers on damage that actually landed, and sends back what was
    // taken rather than what was aimed, so overkill cannot be bounced back. The
    // bounce is an ordinary attack — the attacker's own minions soak it first. The
    // shield is spent before the bounce, so two mirrors cannot ping-pong forever.
    if (!player.reflect || !source || source.id === player.id) return;
    player.reflect = false;
    attackPlayer(player, source, taken);
  }

  function removeSips(player, amount) {
    const before = player.sipsTaken;
    player.sipsTaken = Math.max(0, player.sipsTaken - amount);
    if (before > player.sipsTaken) recordHeal(player, before - player.sipsTaken);
  }

  // placedBy is whoever played the card. Snyltgästen is planted on an enemy's board, so
  // the minion has to remember who to credit for the sips it makes its host drink.
  function summonMinion(player, name, hp, ability = null, placedBy = null) {
    if (player.minions.length >= MAX_MINIONS) player.minions.shift();
    player.minions.push({ id: makeId(), name, hp, maxHp: hp, ability, placedBy: placedBy?.id ?? null });
    if (placedBy && placedBy.id !== player.id) narrate(`${placedBy.name} sätter ${name} hos ${player.name}.`);
    else narrate(`${player.name} placerar ${name}. Den måste slås ut först.`);
  }

  // One minion takes up to `amount` and answers how much it soaked. Shared by attacks,
  // which work down the board from the front, and by Skumpartyt-style cards that hit
  // every minion at once.
  function strikeMinion(owner, minion, amount, source) {
    const wasAlive = minion.hp > 0;
    // A minion at or below zero can only come from a forged state. It soaks nothing,
    // and must not hand damage back by making the hit negative.
    const hit = Math.max(0, Math.min(minion.hp, amount));
    minion.hp -= hit;
    const killed = minion.hp <= 0;
    narrate(`${minion.name} skyddar ${owner.name} och tar ${hit} damage.`);
    recordMinionEvent(owner, minion, hit, killed, source);
    // Off the board before any side effect runs: a prick or a pop can bounce back
    // through a mirror into attackPlayer, and the bounce has to find the next minion,
    // not this dead one a second time.
    if (killed) owner.minions.splice(owner.minions.indexOf(minion), 1);
    // Spikes: touching this minion costs a sip, whether it survives or not. The
    // narration is what keeps the prick from reading as a mystery drink in the log.
    if (minion.ability === "thorns" && source) {
      narrate(`${minion.name}s spikar sticker ${source.name} — 1 klunk.`);
      addSips(source, 1, owner);
    }
    if (minion.ability === "popper" && killed && wasAlive && source) {
      narrate(`${minion.name} smäller — ${source.name} tar ${POPPER_SIPS} klunkar.`);
      addSips(source, POPPER_SIPS, owner);
    }
    if (killed && wasAlive) minionFell(owner, minion);
    return hit;
  }

  function minionFell(owner, minion) {
    if (minion.ability === "cooler") {
      narrate(`${minion.name} går sönder — ${owner.name} tar bort ${sipsText(COOLER_HEAL)}.`);
      removeSips(owner, COOLER_HEAL);
    }
    if (owner.minions.some((other) => other.ability === "photographer" && other.hp > 0)) {
      narrate(`Fotografen fångar ${minion.name} — ${owner.name} drar ett kort.`);
      drawCard(owner);
    }
  }

  // Every minion on one board takes `amount`, nothing spills onto the owner.
  function hitMinions(owner, amount, source) {
    for (const minion of [...owner.minions]) {
      if (minion.hp > 0) strikeMinion(owner, minion, amount, source);
    }
  }

  // Answers whether the attack connected, so a card can make a follow-up depend on it
  // (Bakfyllan's hangover does not stick if the umbrella caught the hit, nor if a
  // two-way mirror sent it somewhere else).
  function attackPlayer(source, target, amount) {
    if (!target || amount <= 0) return false;
    const fromElsewhere = Boolean(source && source.id !== target.id);
    // Paraplydrinken: the next attack from somebody else misses outright — minions and
    // all. Pierce (addSips), thorns and fatigue never come through here, so they go
    // around the umbrella.
    if (target.dodge && fromElsewhere) {
      target.dodge = false;
      narrate(`${target.name}s paraply tar ${source.name}s attack — inga klunkar.`);
      recordBlock(target, amount);
      return false;
    }
    // Tvåvägsspegeln: the whole attack goes on to somebody else, or back to the attacker
    // when there is nobody. Each mirror is spent as it passes the hit on, so a chain of
    // them ends.
    if (target.rebound && fromElsewhere) {
      target.rebound = false;
      const next = pickRebound(target, source) || source;
      narrate(`${target.name}s tvåvägsspegel skickar ${source.name}s attack vidare till ${next.name}.`);
      recordBlock(target, amount);
      attackPlayer(source, next, amount);
      return false;
    }
    // Glasunderlägget: the next attack is two lighter.
    if (target.armor > 0 && fromElsewhere) {
      const cut = Math.min(target.armor, amount);
      target.armor = 0;
      amount -= cut;
      narrate(`${target.name}s glasunderlägg tar ${sipsText(cut)}.`);
      recordBlock(target, cut);
      if (amount <= 0) return true;
    }
    let overflow = amount;
    while (overflow > 0 && target.minions.length) {
      const minion = target.minions[0];
      overflow -= strikeMinion(target, minion, overflow, source);
      // A minion that survives eats the whole hit; overkill carries on to whatever
      // is behind it.
      if (minion.hp > 0) overflow = 0;
    }
    if (overflow > 0) addSips(target, overflow, source);
    return true;
  }

  // End rather than start of the owner's turn, so a minion summoned this turn already
  // acts. A pinger throws 1 sip at one random living enemy — an ordinary attack, so the
  // victim's minions soak it first. A bartender takes a sip off its owner; a home
  // brewer grows. Only the device that advances the turn runs this — same single-writer
  // rule as the bot driver, or two devices would roll two different targets for one ping.
  function triggerTurnEndMinions({ owner, enemies = [], allies = [], randomInt } = {}) {
    if (!owner) return;
    for (const minion of [...owner.minions]) {
      // A zero-hp minion can only arrive from a forged state, or die to a bounced ping
      // earlier in this loop; either way it stays silent.
      if (minion.hp <= 0) continue;
      if (minion.ability === "pinger" && enemies.length) {
        const target = enemies[randomInt(0, enemies.length - 1)];
        narrate(`${minion.name} skickar 1 klunk till ${target.name}.`);
        attackPlayer(owner, target, 1);
      } else if (minion.ability === "bartender" && owner.sipsTaken > 0) {
        narrate(`${minion.name} serverar vatten — ${owner.name} tar bort 1 klunk.`);
        removeSips(owner, 1);
      } else if (minion.ability === "brew" && minion.hp < BREW_CAP) {
        minion.hp += 1;
        minion.maxHp = Math.max(minion.maxHp, minion.hp);
        narrate(`${minion.name} jäser till ${minion.hp} HP.`);
      } else if (minion.ability === "dj" && enemies.length) {
        narrate(`${minion.name} spelar en plåga — alla andra tar 1 klunk.`);
        enemies.forEach((enemy) => attackPlayer(owner, enemy, 1));
      } else if (minion.ability === "tower" && enemies.length) {
        const target = enemies[randomInt(0, enemies.length - 1)];
        narrate(`${minion.name} rasar över ${target.name} — 2 klunkar.`);
        attackPlayer(owner, target, 2);
      } else if (minion.ability === "melt") {
        minion.hp -= MELT;
        if (minion.hp > 0) {
          narrate(`${minion.name} smälter till ${minion.hp} HP.`);
        } else {
          narrate(`${minion.name} har smält bort.`);
          owner.minions.splice(owner.minions.indexOf(minion), 1);
        }
      } else if (minion.ability === "captain") {
        narrate(`${minion.name} samlar laget — alla tar bort 1 klunk.`);
        [owner, ...allies].forEach((player) => removeSips(player, 1));
      }
    }
  }

  // At the start of its host's turn, every Snyltgästen makes them drink 1 — straight
  // past their other minions, since it already sits inside the defences. findPlayer
  // resolves the planter for attribution; a planter who has left credits nobody.
  // Fyllekompisen does the same and then wanders on to one of hostsFor(minion) — the
  // planter's other enemies — and Kassören rings up a mana.
  function triggerTurnStartMinions({ owner, findPlayer = () => null, hostsFor = () => [], randomInt = () => 0 } = {}) {
    if (!owner) return;
    for (const minion of [...owner.minions]) {
      if (minion.hp <= 0) continue;
      if (minion.ability === "cashier") {
        owner.mana += 1;
        narrate(`${minion.name} slår in 1 mana åt ${owner.name}.`);
        continue;
      }
      if (minion.ability !== "leech" && minion.ability !== "drifter") continue;
      narrate(`${minion.name} får ${owner.name} att dricka 1 klunk.`);
      addSips(owner, 1, findPlayer(minion.placedBy) || null);
      if (minion.ability !== "drifter") continue;
      const hosts = hostsFor(minion).filter((player) => player.id !== owner.id);
      if (!hosts.length) continue;
      const next = hosts[randomInt(0, hosts.length - 1)];
      owner.minions.splice(owner.minions.indexOf(minion), 1);
      if (next.minions.length >= MAX_MINIONS) next.minions.shift();
      next.minions.push(minion);
      narrate(`${minion.name} vinglar vidare till ${next.name}.`);
    }
  }

  // The drinks a turn start owes, after the minions: Bakfyllan's hangover in one go,
  // then one sip of a Långdrink. Both go straight past the player's minions and both are
  // credited to whoever set them up, so a mirror can bounce them back.
  function triggerTurnStartDrinks({ owner, findPlayer = () => null } = {}) {
    if (owner.hangover > 0) {
      const amount = owner.hangover;
      const sender = findPlayer(owner.hangoverBy);
      owner.hangover = 0;
      owner.hangoverBy = null;
      narrate(`${owner.name} vaknar bakfull och dricker ${sipsText(amount)}.`);
      addSips(owner, amount, sender);
    }
    if (owner.drip > 0) {
      const sender = findPlayer(owner.dripBy);
      owner.drip -= 1;
      if (!owner.drip) owner.dripBy = null;
      narrate(`${owner.name} dricker 1 klunk till av långdrinken.`);
      addSips(owner, 1, sender);
    }
  }

  return {
    addSips,
    attackPlayer,
    drainNarration,
    hitMinions,
    removeSips,
    summonMinion,
    triggerTurnEndMinions,
    triggerTurnStartDrinks,
    triggerTurnStartMinions,
  };
}
